import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import type { SpassuCodeApi } from '../../api';
import type {
  AzureWorkItemDetail,
  AzureWorkItemSummary,
} from '../../api/types';
import type { ActivityCreationContentAction } from '../../activity-creation/activityCreationReducer';

interface WorkItemContext {
  open: boolean;
  provider: string | undefined;
  project: string;
  mode: string;
}

function cardContent(detail: AzureWorkItemDetail) {
  const parts: string[] = [];
  if (detail.descriptionText) parts.push(detail.descriptionText);
  else if (detail.reproStepsText) parts.push(detail.reproStepsText);
  if (detail.acceptanceCriteriaText) {
    parts.push(`## Critérios de Aceitação\n\n${detail.acceptanceCriteriaText}`);
  }
  return {
    provider: 'azure-devops',
    externalId: String(detail.id),
    title: detail.title,
    context: parts.join('\n\n'),
  };
}

function selectionError(cause: unknown) {
  const message = cause instanceof Error ? cause.message : String(cause);
  if (message === 'WORK_ITEM_NOT_FOUND')
    return 'Work Item não encontrado. Pode ter sido removido.';
  if (message === 'AZURE_PERMISSION_DENIED')
    return 'Permissão negada ao acessar o Work Item. Verifique o escopo do PAT.';
  return `Erro ao carregar Work Item: ${message}`;
}

export function useWorkItemSelection(
  api: SpassuCodeApi,
  context: WorkItemContext,
  dispatch: Dispatch<ActivityCreationContentAction>,
  setError: (error: string) => void,
) {
  const [selectedWorkItem, setSelectedWorkItem] =
    useState<AzureWorkItemDetail | null>(null);
  const generation = useRef(0);
  const invalidate = useCallback(() => {
    generation.current += 1;
  }, []);
  const clearWorkItem = useCallback(() => {
    invalidate();
    setSelectedWorkItem(null);
    dispatch({ type: 'card.removed' });
  }, [dispatch, invalidate]);
  const { open, provider, project, mode } = context;
  // Invalidate at commit, including close/reopen and returning to the same scope.
  useLayoutEffect(() => {
    clearWorkItem();
    return invalidate;
  }, [api, open, provider, project, mode, clearWorkItem, invalidate]);
  const handleSelectWi = useCallback(
    async (summary: AzureWorkItemSummary) => {
      const request = ++generation.current;
      try {
        const detail = await api.getWorkItem(project, summary.id);
        if (request !== generation.current) return;
        setSelectedWorkItem(detail);
        dispatch({ type: 'card.selected', card: cardContent(detail) });
      } catch (cause) {
        if (request === generation.current) setError(selectionError(cause));
      }
    },
    [api, project, dispatch, setError],
  );
  return { selectedWorkItem, clearWorkItem, handleSelectWi };
}
